import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Bump_screen } from 'src/app/models/bump-screen.model';
import { CommsService } from '../../utils/comms/comms.service';
import { StorageService } from '../../utils/storage/storage.service';
import { ACTIVE_BUMP_SCREEN_CHANGED, LOG_IN, LOG_OUT } from '../../utils/watcher/watcher-event';
import { WatcherService } from '../../utils/watcher/watcher.service';
import { CoreService } from '../core/core.service';

import * as moment from 'moment-timezone';

import has from 'lodash/has';
import map from 'lodash/map';
import size from 'lodash/size';
import { SmartlookService } from '../../utils/smartlook/smartlook';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  /** Flagger for when the service is still trying to authenticate user */
  private _signingIn: boolean = false;

  /** The header object to pass as part of HTTPHeaders */
  public headerData: any = {};

  /** The collection of all bump screens linked to the account */
  private _bump_screens: Bump_screen[] = [];

  /** The activated bump screen to use */
  private _active_bump_screen: Bump_screen;

  /** The user node ID */
  private _user_id: number;

  /** Flagger to check if user is logged in */
  private _logged_in: boolean = false;

  /** Flagger used if the app will clear the cache on next admin link open */
  private _new_user: boolean = true;

  /** The user account email */
  private _user_email: string;

  /** Flagger used if the app is going to store the bump screen */
  public toRememberFlag: boolean = true;

  /** Admin PIN */
  private _admin_pin: string = '';

  constructor(
    public comms: CommsService,
    public core: CoreService,
    public storage: StorageService,
    public watcher: WatcherService,
    public smartlook: SmartlookService,
  ) {}

  /**
   * Calls the server for verify the user's credentials
   * @param data - The user credentials object
   * @param allowLoading - Flagger to broadcast the GLOBAL login sign in loader
   * @returns - returns a promise the resolves the HTTP response payload
   */
  authenticate(data: any, allowLoading: boolean = true): Promise<any> {
    return new Promise((resolve, reject) => {
      this._signingIn = false;

      // set the header
      if (has(data, 'access_key') && has(data, 'id')) {
        this.headerData['Authorization'] = 'Bearer ' + data.access_key;
        delete data.access_key;
      }

      this._signingIn = allowLoading;

      // attach device information
      data['device'] = {
        version: this.core.appDeviceInfo.device.version,
        uuid: this.core.appDeviceInfo.device.uuID,
        manufacturer: this.core.appDeviceInfo.device.manufacturer,
        platform: this.core.appDeviceInfo.device.platform,
        platformVersion: this.core.appDeviceInfo.device.version,
        model: this.core.appDeviceInfo.device.model,
        app: 'Ready to Serve',
      };

      const header = new HttpHeaders(this.headerData);
      this.comms.post(
        this.core.Active_server.url + 'api/bump/login/',
        data,
        {
          header: header,
        },
        payload => {
          this._signingIn = false;
          resolve(payload);
        },
        fail => {
          this._signingIn = false;
          resolve(fail);
        },
        30000,
      );
    });
  }

  /**
   * Calls the server to reset the password of the user/email
   * @param email - The email to reset the password
   * @returns - returns a promise the resolves the HTTP response payload
   */
  resetPassword(email: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.comms.post(
        this.core.Active_server.url + 'api/oohub/validate-email',
        {
          email: email,
        },
        {
          header: {},
        },
        payload => {
          resolve(payload);
        },
        fail => {
          resolve(fail);
        },
      );
    });
  }

  /**
   * Sets the bump screen to use. The function will store the selected bump scren
   * in storage and will expire in 7 days and will be up for verification until then.
   *
   * @param bumpScreen - The bump screen to activate
   */
  public setBumpScreen(bumpScreen: Bump_screen): void {
    this._active_bump_screen = bumpScreen;

    this.headerData['Authorization'] = 'Bearer ' + bumpScreen.access_key;

    // set the location timezone to use
    this.core.Timezone = bumpScreen.location_details[0].timezone.replace('|', '/');

    // always update the expiration
    bumpScreen.expiry = moment().add(7, 'days').valueOf();

    // store the access key and bump id
    if (this.toRememberFlag) {
      this.storage.save('screen', bumpScreen.export());
    }

    this._logged_in = true;

    // fire a global update
    this.watcher.broadcast(ACTIVE_BUMP_SCREEN_CHANGED);
    this.smartlook.startRecording(bumpScreen.id);
  }

  /**
   * Stores all the available bump screens of the account in a variable and in storage
   * @param screens - The collection of bump screens linked to the account
   */
  public saveAllBumpScreens(screens: Bump_screen[]): void {
    this._bump_screens = screens;

    if (size(this._bump_screens)) {
      const screenStorage: any[] = map(screens, (screen: Bump_screen) => {
        return screen.export();
      });

      // store the access key and bump id
      this.storage.save('account_screens', screenStorage);
    } else {
      this.storage.remove('account_screens');
    }
  }

  /**
   * Logs out the user and clears any fragments of user data
   */
  logout(): Promise<any> {
    this._signingIn = false;
    this._active_bump_screen = null;
    this._user_id = null;
    this._logged_in = false;
    this._bump_screens = [];
    this.toRememberFlag = true;
    this._admin_pin = '';

    this.watcher.broadcast(LOG_OUT);

    return Promise.all([
      this.storage.remove('screen'),
      this.storage.remove('user_access'),
      this.storage.remove('account_screens'),
    ]);
  }

  /**
   * Logs out the user and clears any fragments of user data
   */
  async userLogin(userId: number) {
    this._user_id = userId;

    this.watcher.broadcast(LOG_IN);

    this.storage.save('user_access', {
      id: this._user_id,
    });

    let savedAdminPin: string = await this.storage.load(this.User_id + '_admin_pin', true);
    if (savedAdminPin) {
      this.Admin_pin = atob(savedAdminPin);
    }
  }

  public get Tracking_key(): string {
    if (this._active_bump_screen) {
      return `${this.core.Active_server.env} - ${this._active_bump_screen.account} - ${this._user_id}`;
    } else {
      return `${this.core.Active_server.env}`;
    }
  }

  public get Active_bump_screen(): Bump_screen {
    return this._active_bump_screen;
  }

  public get Signing_in(): boolean {
    return this._signingIn;
  }

  public get Bump_screens(): Bump_screen[] {
    return this._bump_screens;
  }

  public get User_id(): number {
    return this._user_id;
  }

  public set New_user(val: boolean) {
    this._new_user = val;
  }
  public get New_user(): boolean {
    return this._new_user;
  }

  public set Email(val: string) {
    this._user_email = val;

    if (val) {
      this.storage.save('email', val);
    } else {
      this.storage.remove('email');
    }
  }
  public get Email(): string {
    return this._user_email;
  }

  public get Logged_in(): boolean {
    return this._logged_in;
  }

  public set Admin_pin(pin: string) {
    this._admin_pin = pin;
  }
  public get Admin_pin(): string {
    return this._admin_pin;
  }
}
